import React, { useEffect } from 'react';
import { patchRequest } from 'helpers/axios';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { setLoader } from 'redux/slices/loader';
import {
  error as renderError,
  success as renderSuccess,
} from 'helpers/toaster.js';
import { ROUTES } from 'constants/routes';
import { getCurrentSubscription } from 'services/subscriptionService';
import { actions } from 'redux/slices/auth';

function InviteAccept() {
  const { token } = useParams() || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const url = new URL(window.location.href);
  const type = url.searchParams.get('type');
  const resource_slug = url.searchParams.get('resource_slug');
  const isAppSumoTier3 = url.searchParams.get('appsumo_tier3') === 'true';

  // For AppSumo tier 3, construct the URL manually, otherwise use the existing implementation
  const acceptURL = isAppSumoTier3
    ? `external/user_invites/${token}/accept`
    : window.location.href.split(`${window.location.origin}/`)[1];

  useEffect(() => {
    const handleInviteAcceptance = async () => {
      try {
        dispatch(
          setLoader({
            isLoading: true,
            heading: 'Accepting Invitation!',
            description: 'Please wait...',
          }),
        );

        // First accept the invitation
        const res = await patchRequest(
          acceptURL,
          isAppSumoTier3 ? { type, resource_slug } : undefined,
        );

        // Check if the response was successful
        if (res?.data?.resource) {
          const { resource } = res.data;

          // Then fetch and update subscription state
          const currentSubscriptionRes = await getCurrentSubscription();
          const { data: subscriptionData } = currentSubscriptionRes || {};

          if (subscriptionData) {
            dispatch(
              actions.updateUser({
                user: { subscriptions: subscriptionData },
              }),
            );
          }

          dispatch(setLoader({ isLoading: false }));

          // Navigate based on type after subscription is updated
          if (type === 'document') {
            const targetRoute = resource.template_id
              ? ROUTES.EditorDocumentTemplate(
                  resource.slug,
                  resource.template_id,
                )
              : ROUTES.FreestyleDocumentTemplate(resource.slug);
            navigate(targetRoute);
          } else if (type === 'team') {
            navigate(`/teams/${resource.slug}`);
          } else if (type === 'folder') {
            navigate(`/folder/${resource.slug}`);
          }

          renderSuccess(res?.data?.msg);
        } else if (isAppSumoTier3) {
          // Handle AppSumo tier 3 specific response
          if (res?.data?.msg === 'Invitation accepted') {
            // Fetch and update subscription state before navigating
            const currentSubscriptionRes = await getCurrentSubscription();
            const { data: subscriptionData } = currentSubscriptionRes || {};
            if (subscriptionData) {
              dispatch(
                actions.updateUser({
                  user: { subscriptions: subscriptionData },
                }),
              );
            }
            dispatch(setLoader({ isLoading: false }));
            renderSuccess(res.data.msg);
            navigate('/teams');
          } else if (res?.data?.msg === 'User not found') {
            // User doesn't exist yet - redirect to sign-up with email pre-filled
            const inviteEmail = res?.data?.email || '';
            dispatch(setLoader({ isLoading: false }));
            renderSuccess('Please create an account to join the team.');
            navigate(`/sign-up${inviteEmail ? `?email=${encodeURIComponent(inviteEmail)}` : ''}`);
          } else if (res?.data?.msg === 'Failed to process invitation. Please try again.') {
            dispatch(setLoader({ isLoading: false }));
            renderError(res.data.msg);
            navigate('/teams');
          } else {
            dispatch(setLoader({ isLoading: false }));
            navigate('/');
          }
        } else {
          // If no resource in response, just navigate to home
          dispatch(setLoader({ isLoading: false }));
          navigate('/');
        }
      } catch (error) {
        dispatch(setLoader({ isLoading: false }));
        if (isAppSumoTier3 && error?.response?.status === 404) {
          // User doesn't exist yet - redirect to sign-up with email pre-filled
          const inviteEmail = error?.response?.data?.email || '';
          renderSuccess('Please create an account to join the team.');
          navigate(`/sign-up${inviteEmail ? `?email=${encodeURIComponent(inviteEmail)}` : ''}`);
        } else if (isAppSumoTier3 && error?.response?.status === 422) {
          const errorMsg = error?.response?.data?.msg || 'Failed to process invitation';
          renderError(errorMsg);
          navigate('/teams');
        } else {
          navigate('/');
        }
      }
    };

    handleInviteAcceptance();
  }, [
    token,
    dispatch,
    navigate,
    acceptURL,
    type,
    resource_slug,
    isAppSumoTier3,
  ]);

  return <></>;
}

export default InviteAccept;
